/**
 * Branch access helpers — Super Admin (role 1) may use any branch;
 * everyone else is locked to their login branch_id.
 *
 * Social Leads is the exception: SMM (16) and Sales (4) must work
 * F-11 and G-13 on that page only. Do not fold those roles into
 * isCrossBranchAdmin or they would unlock every report/sales screen.
 */
import secureLocalStorage from "react-secure-storage";

export const isCrossBranchAdmin = (role) => String(role ?? "") === "1";

/**
 * Employee attendance only. HR (12) may pick F-11, G-13, or all branches
 * on that page. Do not add 12 to isCrossBranchAdmin.
 */
export const ATTENDANCE_BRANCH_PICKER_ROLES = ["12"];

export const canUseAllBranches = (role) => isCrossBranchAdmin(role);

/** G-13 = 1, F-11 = 15 */
export const SOCIAL_LEAD_BRANCH_IDS = ["1", "15"];

export const SOCIAL_LEAD_CROSS_BRANCH_ROLES = ["1", "3", "4", "16"];

export const canUseSocialLeadAllBranches = (role) =>
  SOCIAL_LEAD_CROSS_BRANCH_ROLES.includes(String(role ?? ""));

/** Keep F-11 + G-13 for social-leads dropdowns; fall back to the full list. */
export const filterSocialLeadBranches = (branches = []) => {
  const gym = (branches || []).filter((b) =>
    SOCIAL_LEAD_BRANCH_IDS.includes(String(b?.id ?? b?.branch_id ?? ""))
  );
  return gym.length ? gym : branches || [];
};

/** Read role / branch from secure storage (works outside React context). */
export const getStoredAuthScope = () => {
  try {
    const roleRaw = secureLocalStorage.getItem("role");
    const branchRaw = secureLocalStorage.getItem("branchId");
    return {
      role: roleRaw ? JSON.parse(roleRaw) : null,
      branchId: branchRaw ? JSON.parse(branchRaw) : null,
    };
  } catch (_) {
    return { role: null, branchId: null };
  }
};

/** Force effective branch for API calls / filters. */
export const resolveUiBranchId = (role, loginBranchId, selectedBranchId) => {
  if (isCrossBranchAdmin(role)) {
    return selectedBranchId === "" ||
      selectedBranchId == null ||
      selectedBranchId === "all"
      ? loginBranchId
      : selectedBranchId;
  }
  return loginBranchId;
};

/** Filter branch dropdown options for non-admin users. */
export const filterBranchOptions = (role, loginBranchId, branches = []) => {
  if (isCrossBranchAdmin(role)) {
    return branches;
  }
  const id = String(loginBranchId ?? "");
  return (branches || []).filter((b) => String(b?.id) === id);
};

/**
 * Scope a branch list using stored auth (for UserServices wrappers).
 * Leaves list unchanged when role is missing or Super Admin.
 */
export const scopeBranchList = (branches = []) => {
  const { role, branchId } = getStoredAuthScope();
  if (!role || isCrossBranchAdmin(role) || branchId == null || branchId === "") {
    return branches;
  }
  return filterBranchOptions(role, branchId, branches);
};

/**
 * Apply scope to common API response shapes for branch lists.
 * Mutates and returns the axios response for chainability.
 */
export const scopeBranchApiResponse = (response) => {
  try {
    const payload = response?.data;
    if (!payload) return response;

    if (Array.isArray(payload.data)) {
      payload.data = scopeBranchList(payload.data);
    } else if (Array.isArray(payload.data?.data)) {
      payload.data.data = scopeBranchList(payload.data.data);
    } else if (Array.isArray(payload)) {
      response.data = scopeBranchList(payload);
    }
  } catch (_) {
    /* keep original response */
  }
  return response;
};
